import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { getCreator, CREATORS } from "../data/creators";
import StoryViewer from "../components/StoryViewer";
import JoinAgencyButton from "../components/JoinAgencyButton";
import SocialIcons from "../components/SocialIcons";

export default function CreatorProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const creator = getCreator(slug);
  const [viewerOpen, setViewerOpen] = useState(false);

  if (!creator) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#0a0a0a] text-white">
        <p className="font-serif-lux text-3xl font-light">Creator not found.</p>
        <Link to="/" className="mt-6 text-[11px] uppercase tracking-[0.3em] text-white/50 hover:text-white">
          Back home
        </Link>
      </div>
    );
  }

  const startIndex = CREATORS.findIndex((c) => c.slug === slug);

  return (
    <div className="grain relative min-h-screen bg-[#0a0a0a] pb-24 text-white">
      {/* back */}
      <button
        onClick={() => navigate(-1)}
        data-testid="creator-back"
        className="fixed left-5 top-5 z-40 flex h-10 w-10 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-md transition-colors hover:bg-black/70"
      >
        <ArrowLeft size={18} strokeWidth={1.6} />
      </button>

      {/* cover hero */}
      <section className="relative h-[65vh] w-full overflow-hidden md:h-[72vh]">
        <img src={creator.cover} alt={creator.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/30 to-black/40" />
        <div
          className="absolute inset-0 opacity-40 mix-blend-overlay"
          style={{ background: `radial-gradient(circle at 30% 40%, ${creator.accent}, transparent 60%)` }}
        />

        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-5xl px-6 pb-10 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="mb-5 flex items-end gap-4">
              <div className="rounded-full p-[3px]" style={{ background: `linear-gradient(135deg, ${creator.accent}, #ffffff)` }}>
                <img src={creator.avatar} alt="" className="h-20 w-20 rounded-full border-2 border-[#0a0a0a] object-cover md:h-24 md:w-24" />
              </div>
              <div className="pb-1">
                <p className="text-[11px] uppercase tracking-[0.3em] text-white/50">{creator.location}</p>
              </div>
            </div>
            <h1 className="font-serif-lux text-5xl font-light leading-none tracking-tight md:text-7xl" data-testid="creator-name">
              {creator.name}
            </h1>
            <p className="mt-3 text-sm tracking-wide text-white/60">{creator.handle}</p>
            <p className="mt-4 max-w-xl font-serif-lux text-xl italic text-white/70">{creator.tagline}</p>

            <button
              onClick={() => setViewerOpen(true)}
              data-testid="creator-watch-story"
              className="group mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-[11px] uppercase tracking-[0.25em] text-black transition-colors hover:bg-white/80"
            >
              Watch Story
              <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* stats */}
      <section className="mx-auto max-w-5xl px-6 py-12 md:px-12">
        <div className="grid grid-cols-3 divide-x divide-white/10 border-y border-white/10">
          {Object.entries(creator.stats).map(([k, v]) => (
            <div key={k} className="px-4 py-8 text-center">
              <p className="font-serif-lux text-3xl font-light md:text-5xl" style={{ color: creator.accent }}>{v}</p>
              <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-white/40">{k}</p>
            </div>
          ))}
        </div>
      </section>

      {/* bio */}
      <section className="mx-auto max-w-3xl px-6 pb-12 text-center md:px-12">
        <p className="text-lg font-light leading-relaxed tracking-wide text-white/70 md:text-xl">{creator.bio}</p>
      </section>

      {/* story highlights */}
      <section className="mx-auto max-w-5xl px-6 pb-16 md:px-12">
        <div className="mb-8 flex items-center justify-between border-b border-white/10 pb-5">
          <h2 className="font-serif-lux text-2xl font-light tracking-tight md:text-3xl">Highlights</h2>
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">Tap to play</span>
        </div>
        <div className="grid grid-cols-3 gap-3 md:gap-5">
          {creator.stories.map((s, i) => (
            <button
              key={i}
              data-testid={`creator-highlight-${i}`}
              onClick={() => setViewerOpen(true)}
              className="group relative aspect-[9/16] overflow-hidden rounded-xl border border-white/10"
            >
              <img src={s.image} alt="" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <p className="absolute inset-x-0 bottom-0 p-3 text-left text-xs font-light text-white/80 line-clamp-2">{s.caption}</p>
            </button>
          ))}
        </div>
      </section>

      {/* join CTA */}
      <section className="mx-auto max-w-3xl px-6 pb-24 text-center md:px-12">
        <div className="border border-white/10 bg-white/[0.02] p-10 md:p-14">
          <h2 className="font-serif-lux text-3xl font-light tracking-tight md:text-4xl">Think you're next?</h2>
          <p className="mx-auto mt-4 max-w-md text-sm font-light leading-relaxed text-white/50">
            We're always scouting bold new voices. Put yourself on our radar.
          </p>
          <JoinAgencyButton
            testid="creator-join-button"
            className="mt-8 inline-flex items-center justify-center rounded-full bg-white px-10 py-4 text-[11px] uppercase tracking-[0.3em] text-black transition-colors hover:bg-white/80"
          />
        </div>
      </section>

      {/* pinned social bar */}
      <div
        data-testid="creator-social-bar"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[#0a0a0a]/85 backdrop-blur-lg"
      >
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4 md:px-12">
          <span className="font-serif-lux text-sm tracking-wide text-white/70">{creator.handle}</span>
          <SocialIcons socials={creator.socials} size={20} className="gap-7" testidPrefix="creator-social" />
        </div>
      </div>

      {viewerOpen && (
        <StoryViewer creators={CREATORS} startIndex={startIndex} onClose={() => setViewerOpen(false)} />
      )}
    </div>
  );
}
